/**
 * S01 · Session Hub — the field operator's home.
 *
 * Answers four questions with real local data, in order: what active
 * session can I resume, what is the sync state, what can I capture right
 * now, and what needs review. Everything reads through the TanStack Query
 * hooks against SQLite; the only fixture that ever lived here — the
 * greeting — now comes from the signed-in profile.
 */
import type { CSSProperties, ReactNode } from 'react';
import { useMemo } from 'react';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import type { Capture, CaptureKind, SessionSummary } from '@kapture/types';
import { sourceMarker } from '@kapture/ui/tokens';
import { Chip, GestureBar, I, Ico, ScreenFrame, StatusBar, T } from '@/components/primitives';
import { BottomNav } from '@/components/BottomNav';
import { ActiveProjectBand } from '@/components/ActiveProjectBand';
import {
  useActiveProject,
  useAuthStore,
  useCaptureSessionStore,
  useCapturesBySession,
  useSessions,
  useSyncStatus,
} from '@kapture/client-core';
import { hasOnboarded } from '@/lib/device/permissions';

export const Route = createFileRoute('/')({
  beforeLoad: () => {
    // First-run gate — bounce the user through S26 Permissions unless
    // they've already granted (or explicitly skipped) on this device.
    if (typeof window !== 'undefined' && !hasOnboarded()) {
      throw redirect({ to: '/onboarding/permissions' });
    }
  },
  component: SessionHubPage,
  staticData: { mobile: true },
});

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMin = Math.round((Date.now() - then) / 60_000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  return `${diffD}d ago`;
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function recId(id: string): string {
  return `REC · ${id
    .replace(/[^a-z0-9]/gi, '')
    .slice(0, 8)
    .toUpperCase()}`;
}

function salutation(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

const SOURCE_OF_KIND: Record<CaptureKind, keyof typeof sourceMarker> = {
  photo: 'photo',
  video: 'video',
  voice: 'voice',
  note: 'note',
  gps: 'location',
  barcode: 'form',
  // `form`, because that is where a signature field lives and there is no
  // signature marker in the ledger. Adding one would be a design-system
  // change for a single evidence type.
  signature: 'form',
};

function kindIcon(kind: CaptureKind): string | ReactNode {
  switch (kind) {
    case 'photo':
    case 'video':
      return I.camera;
    case 'voice':
      return I.mic;
    case 'gps':
      return I.pin;
    case 'barcode':
      return I.tag;
    default:
      return I.note;
  }
}

function captureTitle(c: Capture): string {
  switch (c.kind) {
    case 'note':
      return c.title || 'Note';
    case 'voice':
      return c.transcript ? `“${c.transcript}”` : 'Voice note';
    case 'photo':
      return 'Photo';
    case 'gps':
      return 'Location pin';
    case 'barcode':
      return `Asset · ${c.value ?? 'scan'}`;
    default:
      return 'Capture';
  }
}

function SessionHubPage() {
  const { data: sessions, isLoading } = useSessions();
  const { online, pending } = useSyncStatus();
  const { data: activeProject } = useActiveProject();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const active = useMemo(() => {
    const list = (sessions ?? []).filter((s) => s.status === 'active');
    return list.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))[0] ?? null;
  }, [sessions]);

  const others = useMemo(
    () => (sessions ?? []).filter((s) => s.id !== active?.id),
    [sessions, active?.id],
  );

  // With a project set the list splits: what belongs to the project you are
  // standing in, and everything else. Without one it stays a single list —
  // grouping by a context nobody has chosen would be inventing structure.
  const inProject = useMemo(
    () => (activeProject ? others.filter((s) => s.projectId === activeProject.id) : []),
    [others, activeProject],
  );
  const elsewhere = useMemo(
    () => (activeProject ? others.filter((s) => s.projectId !== activeProject.id) : others),
    [others, activeProject],
  );

  const stats = useMemo(() => {
    const list = sessions ?? [];
    return {
      active: list.filter((s) => s.status === 'active').length,
      submitted: list.filter((s) => s.status === 'submitted').length,
      approved: list.filter((s) => s.status === 'approved').length,
      captures: list.reduce((acc, s) => acc + s.captureCount, 0),
    };
  }, [sessions]);

  const isEmpty = !isLoading && (sessions?.length ?? 0) === 0;
  const allDone =
    !isLoading &&
    (sessions?.length ?? 0) > 0 &&
    stats.active === 0 &&
    stats.submitted + stats.approved === (sessions?.length ?? 0);

  const firstName = user?.name?.split(/\s+/)[0];

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '8px 16px 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontSize: 13, color: T.textDim, fontFamily: T.font, fontWeight: 500 }}>
            {firstName ? `${salutation()}, ${firstName}` : salutation()}
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              fontFamily: T.font,
              letterSpacing: '-0.02em',
            }}
          >
            Sessions
          </div>
        </div>
        {/* Submitted work leaves this list, so the way back to it has to
            live on the screen it leaves from. */}
        <Link
          to="/submissions"
          aria-label="My submissions"
          style={{ ...iconBtn, textDecoration: 'none', position: 'relative' }}
        >
          <Ico d={I.send} size={17} />
          {stats.submitted > 0 && (
            <span
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 7,
                height: 7,
                borderRadius: 4,
                background: T.brand,
                border: `1.5px solid ${T.surface}`,
              }}
            />
          )}
        </Link>
      </div>

      <ActiveProjectBand />

      {/* Sync is system state, not an alarm: quiet when healthy, amber with
          the queue count when offline. Always one tap from the queue. */}
      <Link
        to="/sync"
        style={{
          margin: '8px 16px 4px',
          padding: '7px 12px',
          background: online ? T.surface2 : T.warnSoft,
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: `1px solid ${online ? T.stroke : T.warnSoft}`,
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 7,
            height: 7,
            borderRadius: 4,
            background: online ? T.good : T.warn,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            flex: 1,
            fontSize: 11,
            fontFamily: T.mono,
            color: online ? T.textDim : T.warnText,
          }}
        >
          {online
            ? pending > 0
              ? `Online · syncing ${pending} queued`
              : 'Online · synced'
            : pending > 0
              ? `Offline · ${pending} queued — kept on this device`
              : 'Offline · everything up to date'}
        </span>
        <Ico d={I.chev} size={13} stroke={T.textMute} />
      </Link>

      <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px 16px' }}>
        {isLoading && (
          <div style={{ padding: '32px 0', textAlign: 'center', color: T.textMute, fontSize: 13 }}>
            Loading sessions…
          </div>
        )}
        {isEmpty && (
          <EmptyState variant="first-run" onCreate={() => navigate({ to: '/sessions/new' })} />
        )}
        {allDone && (
          <EmptyState variant="all-done" onCreate={() => navigate({ to: '/sessions/new' })} />
        )}

        {active && <ContinueWork session={active} />}

        {active && <RecentEvidence sessionId={active.id} />}

        {inProject.length > 0 && (
          <>
            <div style={sectionLabel}>In this project</div>
            {inProject.map((s) => (
              <SessionRow key={s.id} session={s} />
            ))}
          </>
        )}

        {elsewhere.length > 0 && (
          <>
            <div style={sectionLabel}>
              {inProject.length > 0
                ? 'Everything else'
                : active
                  ? 'Other sessions'
                  : 'All sessions'}
            </div>
            {elsewhere.map((s) => (
              <SessionRow key={s.id} session={s} />
            ))}
          </>
        )}
      </div>

      <BottomNav active="home" captureSessionId={active?.id ?? null} />
      <GestureBar />
    </ScreenFrame>
  );
}

const sectionLabel: CSSProperties = {
  fontSize: 10,
  fontFamily: T.mono,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: T.textMute,
  margin: '14px 2px 6px',
};

const iconBtn: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  background: T.surface2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: T.textDim,
};

/**
 * The Continue-work surface: the one active session, on the dark record
 * ground, with resume as the decisive action and direct capture shortcuts
 * that skip the mode picker.
 */
function ContinueWork({ session }: { session: SessionSummary }) {
  const navigate = useNavigate();
  const setMode = useCaptureSessionStore((s) => s.setMode);
  const setActiveSession = useCaptureSessionStore((s) => s.setActiveSession);

  function quickCapture(kind: CaptureKind, path: string) {
    setActiveSession(session.id);
    setMode(kind);
    navigate({ to: path, params: { sessionId: session.id } });
  }

  return (
    <div
      style={{
        background: T.hudBg,
        color: T.hudInk,
        borderRadius: T.r4,
        padding: 14,
        marginBottom: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            fontSize: 9.5,
            fontFamily: T.mono,
            fontWeight: 500,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: T.brand,
          }}
        >
          {recId(session.id)}
        </span>
        <Chip tone="brandSoft" size="sm">
          ● Active
        </Chip>
        {session.pendingSyncCount > 0 && (
          <span style={{ fontSize: 10, fontFamily: T.mono, color: T.hudInkDim }}>
            {session.pendingSyncCount} queued
          </span>
        )}
      </div>
      <Link
        to="/sessions/$sessionId"
        params={{ sessionId: session.id }}
        preload="intent"
        style={{ textDecoration: 'none', color: 'inherit', display: 'block', marginTop: 8 }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            fontFamily: T.font,
            letterSpacing: '-0.015em',
            lineHeight: 1.3,
          }}
        >
          {session.title}
        </div>
        <div style={{ fontSize: 11, fontFamily: T.mono, color: T.hudInkDim, marginTop: 4 }}>
          {timeAgo(session.updatedAt)} · {session.captureCount} capture
          {session.captureCount === 1 ? '' : 's'}
        </div>
      </Link>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Link
          to="/sessions/$sessionId"
          params={{ sessionId: session.id }}
          preload="intent"
          style={{
            flex: 1,
            minHeight: 44,
            borderRadius: T.r2,
            background: T.brand,
            color: T.brandInk,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            fontFamily: T.font,
            fontWeight: 700,
            fontSize: 13,
            textDecoration: 'none',
          }}
        >
          Resume session
          <Ico d={I.arrow} size={14} />
        </Link>
        {(
          [
            {
              kind: 'photo',
              icon: I.camera,
              label: 'Photo',
              path: '/sessions/$sessionId/capture/photo',
            },
            {
              kind: 'voice',
              icon: I.mic,
              label: 'Voice',
              path: '/sessions/$sessionId/capture/voice',
            },
            {
              kind: 'note',
              icon: I.note,
              label: 'Note',
              path: '/sessions/$sessionId/capture/note',
            },
          ] as const
        ).map((m) => (
          <button
            key={m.kind}
            type="button"
            aria-label={`Capture ${m.label.toLowerCase()} in ${session.title}`}
            onClick={() => quickCapture(m.kind, m.path)}
            style={{
              width: 46,
              minHeight: 44,
              borderRadius: T.r2,
              background: 'rgba(245,244,241,0.08)',
              border: `1px solid ${T.hudLine}`,
              color: T.hudInk,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              cursor: 'pointer',
            }}
          >
            <Ico d={m.icon} size={16} />
            <span style={{ fontSize: 8.5, fontFamily: T.mono, letterSpacing: '0.04em' }}>
              {m.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** The active session's last three captures — the trust check after capture. */
function RecentEvidence({ sessionId }: { sessionId: string }) {
  const { data: captures } = useCapturesBySession(sessionId);
  const recent = useMemo(
    () =>
      [...(captures ?? [])]
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
        .slice(0, 3),
    [captures],
  );
  if (recent.length === 0) return null;

  return (
    <>
      <div style={sectionLabel}>Recent evidence</div>
      {recent.map((c) => {
        const marker = sourceMarker[SOURCE_OF_KIND[c.kind]];
        return (
          <Link
            key={c.id}
            to="/sessions/$sessionId/captures/$captureId"
            params={{ sessionId, captureId: c.id }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: T.r2,
              border: `1px solid ${T.stroke}`,
              background: T.surface,
              marginBottom: 6,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <span
              role="img"
              aria-label={marker.label}
              style={{
                width: 26,
                height: 26,
                borderRadius: 8,
                background: marker.soft,
                color: marker.base,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: `1px solid ${marker.base}40`,
              }}
            >
              <Ico d={kindIcon(c.kind)} size={13} />
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: T.font,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {captureTitle(c)}
            </span>
            <span style={{ fontSize: 10, fontFamily: T.mono, color: T.textMute, flexShrink: 0 }}>
              {clock(c.createdAt)}
            </span>
          </Link>
        );
      })}
    </>
  );
}

function SessionRow({ session }: { session: SessionSummary }) {
  const isActive = session.status === 'active';
  const showPendingBadge = !isActive && session.pendingSyncCount > 0;
  return (
    <Link
      to="/sessions/$sessionId"
      params={{ sessionId: session.id }}
      preload="intent"
      style={{
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
        marginBottom: 8,
      }}
    >
      <div
        style={{
          padding: 12,
          background: T.surface,
          border: `1px solid ${T.stroke}`,
          borderRadius: T.r2,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          ...(isActive
            ? { borderColor: T.brand, boxShadow: '0 0 0 3px rgba(255,90,31,0.08)' }
            : {}),
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            flexShrink: 0,
            background: isActive ? T.brandSoft : T.surface2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isActive ? T.brand : T.textDim,
          }}
        >
          <Ico d={I.folder} size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            {isActive && (
              <Chip tone="brand" size="sm">
                ● Active
              </Chip>
            )}
            {showPendingBadge && (
              <Chip tone="warn" size="sm">
                Pending
              </Chip>
            )}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              fontFamily: T.font,
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {session.title}
          </div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font, marginTop: 2 }}>
            {timeAgo(session.updatedAt)} · {session.captureCount} capture
            {session.captureCount === 1 ? '' : 's'}
          </div>
        </div>
        <Ico d={I.chev} size={16} stroke={T.textMute} />
      </div>
    </Link>
  );
}

type EmptyVariant = 'first-run' | 'all-done';

const EMPTY_COPY: Record<
  EmptyVariant,
  {
    title: string;
    body: string;
    cta: string;
    icon: string | React.ReactNode;
    bg: string;
    fg: string;
  }
> = {
  'first-run': {
    title: 'Start your first capture',
    body: 'Create a session to log photos, video clips, voice notes, and GPS pins — everything is stored offline until you sync.',
    cta: 'New session',
    icon: I.folder,
    bg: T.brandSoft,
    fg: T.brand,
  },
  'all-done': {
    title: 'All caught up',
    body: 'Every active inspection has been submitted. When you walk onto the next site, start a new session.',
    cta: 'Start another',
    icon: I.check2,
    bg: T.goodSoft,
    fg: T.good,
  },
};

function EmptyState({ variant, onCreate }: { variant: EmptyVariant; onCreate: () => void }) {
  const copy = EMPTY_COPY[variant];
  return (
    <div
      style={{
        padding: '40px 12px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 18,
          background: copy.bg,
          color: copy.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 16,
        }}
      >
        <Ico d={copy.icon} size={36} sw={1.6} />
      </div>
      <div
        style={{
          fontSize: 17,
          fontWeight: 700,
          fontFamily: T.font,
          letterSpacing: '-0.01em',
        }}
      >
        {copy.title}
      </div>
      <div
        style={{
          fontSize: 13,
          color: T.textDim,
          fontFamily: T.font,
          marginTop: 6,
          maxWidth: 260,
          lineHeight: 1.5,
        }}
      >
        {copy.body}
      </div>
      <button
        type="button"
        onClick={onCreate}
        style={{
          marginTop: 18,
          padding: '10px 18px',
          minHeight: 44,
          borderRadius: 10,
          background: T.brand,
          color: '#fff',
          fontFamily: T.font,
          fontWeight: 700,
          fontSize: 14,
          border: `1px solid ${T.brand}`,
          cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(255,90,31,0.2)',
        }}
      >
        {copy.cta}
      </button>
      {variant === 'first-run' && (
        <div
          style={{
            marginTop: 20,
            display: 'flex',
            gap: 10,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Shortcut icon={I.camera} label="Photo" />
          <Shortcut icon={I.mic} label="Voice" />
          <Shortcut icon={I.note} label="Note" />
        </div>
      )}
    </div>
  );
}

function Shortcut({ icon, label }: { icon: string | React.ReactNode; label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        width: 64,
        padding: '10px 8px',
        background: T.surface2,
        borderRadius: 10,
        color: T.textDim,
      }}
    >
      <Ico d={icon} size={18} />
      <div style={{ fontSize: 10, fontWeight: 600, fontFamily: T.font }}>{label}</div>
    </div>
  );
}
